import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { en } from "./translations/en";
import { ar } from "./translations/ar";

// ─── Types ────────────────────────────────────────────────────────────────────

export type Language = "en" | "ar";
export type Dir = "ltr" | "rtl";

export interface LanguageContextType {
  language: Language;
  dir: Dir;
  setLanguage: (lang: Language) => void;
  /** Returns a plain string. Falls back to the key if not found or not a string.
   *  Pass `params` to interpolate `{{paramName}}` placeholders in the translated string. */
  t: (key: string, params?: Record<string, string | number>) => string;
  /** Returns an array. Falls back to [] if not found or not an array. */
  tArray: <T = string>(key: string) => T[];
}

const STORAGE_KEY = "arteria_language";

// ─── Context ──────────────────────────────────────────────────────────────────

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // FIX: Persist language preference across reloads
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "en" || saved === "ar") return saved;
    } catch { /* localStorage unavailable */ }
    return "en";
  });

  const dir: Dir = language === "ar" ? "rtl" : "ltr";

  // FIX: Sync <html> dir + lang attributes on every language change
  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
  }, [language, dir]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try { localStorage.setItem(STORAGE_KEY, lang); }
    catch { /* ignore */ }
  }, []);

  /** Returns a plain string for a key. Falls back to the key itself if missing or not a string. */
  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const store = language === "ar" ? ar : en;
    const val = store[key];
    let result: string;
    if (typeof val === "string") {
      result = val;
    } else {
      // FIX: Never silently swallow missing keys in dev
      if (process.env.NODE_ENV === "development") {
        console.warn(`[i18n] Missing string key: "${key}" (lang: ${language})`);
      }
      result = key;
    }
    if (params) {
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replaceAll(`{{${paramKey}}}`, String(paramValue));
      }
    }
    return result;
  }, [language]);

  /** Returns a typed array for a key. Falls back to [] if missing or not an array. */
  const tArray = useCallback(<T = string>(key: string): T[] => {
    const store = language === "ar" ? ar : en;
    const val = store[key];
    if (Array.isArray(val)) return val as T[];
    if (process.env.NODE_ENV === "development") {
      console.warn(`[i18n] Missing array key: "${key}" (lang: ${language})`);
    }
    return [];
  }, [language]);

  const value = useMemo(
    () => ({ language, dir, setLanguage, t, tArray }),
    [language, dir, setLanguage, t, tArray],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside <LanguageProvider>");
  return ctx;
};